import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { tcpLabel } from '@rydres/shared';
import { Notice, Spinner, fmtPhone } from '@rydres/ui';
import { api } from '../lib/api.js';
import { usePassenger } from '../lib/passenger.js';
import type { PublicPage } from '../lib/types.js';
import { Shell } from './Passenger.js';

const CHIP = 'inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-card2 px-4 text-sm font-bold';

/** rydres.com/me/drivers/<handle>: a driver's photograph, business, permit, how to reach them and their car. */
export function DriverProfilePage() {
  const { handle = '' } = useParams();
  const passenger = usePassenger();
  const [page, setPage] = useState<PublicPage | null | undefined>(undefined);
  useEffect(() => {
    api.call<PublicPage>('GET', `/public/pages/${encodeURIComponent(handle)}`, { auth: false }).then(setPage, () => setPage(null));
  }, [handle]);
  if (page === undefined) return <Shell><Spinner /></Shell>;
  if (page === null) return <Shell><Notice tone="warn">There is no driver at this address.</Notice></Shell>;
  const name = page.displayName ?? 'Your driver';
  const mine = passenger.me?.drivers.find((d) => d.handle === page.handle);
  const vehicle = [page.vehicle.year, page.vehicle.color, page.vehicle.make, page.vehicle.model].filter(Boolean).join(' ');
  return (
    <Shell>
      <div className="flex flex-col items-center gap-3 text-center">
        {page.photoUrl ? (
          <img src={page.photoUrl} alt={`${name}, the driver`} className="h-40 w-40 rounded-3xl object-cover shadow-card" />
        ) : (
          <div className="flex h-40 w-40 items-center justify-center rounded-3xl bg-brand text-6xl font-black text-on-brand shadow-card">{name.slice(0, 1)}</div>
        )}
        <div>
          <h1 className="text-2xl font-black">{name}</h1>
          {page.legalName && <p className="text-sm text-ink2">{page.legalName}</p>}
          {page.tcpNumber && <p className="text-xs text-muted">{tcpLabel(page.tcpNumber)}</p>}
        </div>
      </div>
      <Link to={`/me/request/${page.handle}`} className="flex min-h-12 items-center justify-center rounded-2xl bg-brand px-4 py-3 font-bold text-on-brand shadow-soft brand-press">
        Add Ride Service
      </Link>
      <div className="flex gap-2">
        {page.phone && <a href={`tel:${page.phone}`} className={CHIP} aria-label={`Call ${name} at ${fmtPhone(page.phone)}`}>Call {fmtPhone(page.phone)}</a>}
        {mine?.canMessage && <Link to={`/me/people?with=${encodeURIComponent(page.handle)}`} className={CHIP} aria-label={`Message ${name}`}>Message</Link>}
      </div>
      {page.bio && <p className="text-sm text-ink2">{page.bio}</p>}
      {page.serviceArea && <p className="text-sm text-muted">Serving {page.serviceArea}</p>}
      {(vehicle || page.vehiclePhotoUrl) && (
        <section className="space-y-2" aria-label="The car">
          <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">The car</h2>
          {vehicle && <p className="px-1 text-sm font-bold">{vehicle}{page.vehicle.seats ? ` · up to ${page.vehicle.seats} passengers` : ''}</p>}
          {page.vehiclePhotoUrl && <img src={page.vehiclePhotoUrl} alt={vehicle || 'The car'} className="aspect-[16/9] w-full rounded-2xl object-cover" />}
        </section>
      )}
    </Shell>
  );
}
